import {
  GatewayControleQuinquennal,
  GatewayInfoSocieteOrias,
} from "@conformite/gateway";
import {
  Button,
  Card,
  FormField,
  FormGrid,
  FormRow,
  FileInputButton,
  Link,
} from "@endya-conformite/ui-kit";
import ChevronDown from "@endya-conformite/ui-kit/src/assets/icons/chevron-down.svg?react";
import ChevronUp from "@endya-conformite/ui-kit/src/assets/icons/chevron-up.svg?react";
import Download from "@endya-conformite/ui-kit/src/assets/icons/download.svg?react";
import AddIcon from "@endya-conformite/ui-kit/src/assets/icons/plus-circle.svg?react";
import { ControleQuinquennalTemplate } from "@src/controleQuinquennal/ControleQuinquenalTemplate";
import { ControleQuinquennalAxeRemediations } from "@src/controleQuinquennal/adherent/axe/ControleQuinquennalAxeRemediation";
import { axeTypeToLabel } from "@src/controleQuinquennal/controleQuinquennal.helper";
import { format, isAfter, isBefore } from "date-fns";
import { sortBy } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useControleQuinquennalAdminAxe } from "../useControleQuinquennalAdminAxe";
import styles from "./ControleQuinquennalAdminAxe.module.scss";
import { ControleQuinquennalAxeProps } from "./axes/ControleQuinquennalAxe.type";
import { ControleQuinquennalAxeGarantieFinanciereIas } from "./axes/ControleQuinquennalAxeGarantieFinanciere";
import { ControleQuinquennalAxeRcPro } from "./axes/ControleQuinquennalAxeRcPro";
import { ControleQuinquennalAxeAssujettis } from "./axes/assujetti/ControleQuinquennalAxeAssujettis";
import { ControleQuinquennalTicketNotes } from "./ControleQuinquennalTicketNotes";

const axeTypeToReactElement: Record<
  GatewayControleQuinquennal.ControleQuinquennalAxeType,
  (props: ControleQuinquennalAxeProps) => JSX.Element | JSX.Element[] | null
> = {
  ASSUJETTI: ControleQuinquennalAxeAssujettis,
  RCPRO_COA: ControleQuinquennalAxeRcPro(GatewayInfoSocieteOrias.Categorie.COA),
  RCPRO_MIA: ControleQuinquennalAxeRcPro(GatewayInfoSocieteOrias.Categorie.MIA),
  RCPRO_COBSP: ControleQuinquennalAxeRcPro(
    GatewayInfoSocieteOrias.Categorie.COBSP
  ),
  RCPRO_MIOBSP: ControleQuinquennalAxeRcPro(
    GatewayInfoSocieteOrias.Categorie.MIOBSP
  ),
  GARANTIE_FINANCIERE_IAS: ControleQuinquennalAxeGarantieFinanciereIas,
  GARANTIE_FINANCIERE_IOBSP: ControleQuinquennalAxeGarantieFinanciereIas,
};

export function ControleQuinquennalAdminAxePage() {
  const {
    axe,
    axeFiles,
    entreprise,
    avancement,
    controleQuinquennal,
    remediations,
    ticketNotes,
    handleZip,
    handleZipRemediation,
    handleAddFile,
    handleValidateAxe,
    handleDeleteRemediation,
  } = useControleQuinquennalAdminAxe();

  const ComponentAxe = axeTypeToReactElement[axe?.type ?? "ASSUJETTI"];
  const [isValid, setIsValid] = useState(false);
  const sortedRemediations = useMemo(
    () => sortBy(remediations, (r) => new Date(r.creationDate)),
    [remediations]
  );

  // Inverser l'ordre pour l'affichage (la plus récente en premier)
  const sortedRemediationsDescending = useMemo(
    () => [...sortedRemediations].reverse(),
    [sortedRemediations]
  );

  const sortedFiles = useMemo(
    () => sortBy(axeFiles, (file) => new Date(file.createdAt)),
    [axeFiles]
  );

  // Calculer les groupes de fichiers basés sur les dates
  const groupedFiles = useMemo(() => {
    const groups: { [key: string]: typeof sortedFiles } = { initial: [] };

    if (sortedFiles.length === 0) return groups;

    // Le groupe "initial" : fichiers dont la date est antérieure à la première remédiation
    const firstRemediation = sortedRemediations[0];
    if (firstRemediation) {
      const firstRemediationDate = new Date(firstRemediation.creationDate);
      groups.initial = sortedFiles.filter((file) =>
        isBefore(new Date(file.createdAt), firstRemediationDate)
      );
    } else {
      groups.initial = sortedFiles;
    }

    // Pour chaque remédiation, filtrer les fichiers dont la date se situe entre
    // la date de la remédiation et celle de la suivante (ou la date actuelle pour la dernière)
    sortedRemediations.forEach((remediation, index) => {
      const remediationStart = new Date(remediation.creationDate);
      const remediationEnd = sortedRemediations[index + 1]
        ? new Date(sortedRemediations[index + 1].creationDate)
        : new Date();
      groups[remediation.id] = sortedFiles.filter(
        (file) =>
          isAfter(new Date(file.createdAt), remediationStart) &&
          isBefore(new Date(file.createdAt), remediationEnd)
      );
    });

    return groups;
  }, [sortedFiles, sortedRemediations]);

  const [groupStates, setGroupStates] = useState<{ [key: string]: boolean }>(
    {}
  );

  useEffect(() => {
    const newStates: { [key: string]: boolean } = { initial: false };
    sortedRemediations.forEach((remediation, index) => {
      newStates[remediation.id] = index === sortedRemediations.length - 1;
    });
    setGroupStates(newStates);
  }, [sortedRemediations]);

  const toggleGroup = (groupId: string) => {
    setGroupStates((prevStates) => ({
      ...prevStates,
      [groupId]: !prevStates[groupId],
    }));
  };

  useEffect(() => {
    if (!entreprise || !entreprise.siren) return;
    if (!avancement || !avancement[entreprise.siren]) return;
    if (!axe || !axe.type) return;

    const currentAvancement = avancement[entreprise.siren];
    switch (axe.type) {
      case GatewayControleQuinquennal.ControleQuinquennalAxeType.RCPRO_COA:
        setIsValid(currentAvancement.rcProCoa === 100);
        break;
      case GatewayControleQuinquennal.ControleQuinquennalAxeType.RCPRO_MIA:
        setIsValid(false);
        break;
      case GatewayControleQuinquennal.ControleQuinquennalAxeType.RCPRO_COBSP:
        setIsValid(currentAvancement.rcProCobsp === 100);
        break;
      case GatewayControleQuinquennal.ControleQuinquennalAxeType.RCPRO_MIOBSP:
        setIsValid(currentAvancement.rcProMiobsp === 100);
        break;
      case GatewayControleQuinquennal.ControleQuinquennalAxeType
        .GARANTIE_FINANCIERE_IAS:
        setIsValid(currentAvancement.garantieFinanciereIas === 100);
        break;
      case GatewayControleQuinquennal.ControleQuinquennalAxeType
        .GARANTIE_FINANCIERE_IOBSP:
        setIsValid(currentAvancement.garantieFinanciereIobsp === 100);
        break;
      default:
        break;
    }
  }, [avancement, entreprise, axe]);

  if (
    !axe ||
    !entreprise ||
    !controleQuinquennal ||
    !remediations ||
    !avancement ||
    ticketNotes === undefined
  )
    return <div />;

  const formatDate = (
    date: Date | number | string,
    dateFormat = "dd/MM/yyyy"
  ) => format(new Date(date), dateFormat);

  return (
    <ControleQuinquennalTemplate
      title={
        axeTypeToLabel[
          axe?.type ??
            GatewayControleQuinquennal.ControleQuinquennalAxeType.ASSUJETTI
        ]
      }
      backUrl={`/admin/controle-quinquennal/${entreprise.hubspotId}`}
      actions={
        [
          GatewayControleQuinquennal.ControleQuinquennalAxeStatus.EN_COURS,
          GatewayControleQuinquennal.ControleQuinquennalAxeStatus.VALIDE,
        ].includes(axe.status) && (
          <div className={styles.actions}>
            <Link
              to={`/admin/controle-quinquennal/${entreprise.hubspotId}/${axe.idAxe}/remediation`}
            >
              <Button variant="outline-accent" className={styles.remediation}>
                Remédiation
              </Button>
            </Link>
            {axe.status ===
              GatewayControleQuinquennal.ControleQuinquennalAxeStatus
                .EN_COURS && (
              <Button onClick={handleValidateAxe}>Valider la conformité</Button>
            )}
          </div>
        )
      }
      isValid={isValid}
    >
      <div className={styles.container}>
        <ControleQuinquennalTicketNotes
          notes={ticketNotes}
          axeLabel={axeTypeToLabel[axe.type]}
        />
        {remediations.length > 0 && (
          <ControleQuinquennalAxeRemediations
            millesime={controleQuinquennal.millesime}
            remediations={remediations}
            siren={entreprise?.siren ?? ""}
            onDeleteRemediation={(idRemediation) =>
              handleDeleteRemediation(idRemediation).finally(() => null)
            }
          />
        )}
        <FormGrid disableHorizontalGutter>
          <div className="card">
            <FormRow>
              <FormField>
                <div className={styles.complementaire}>
                  <h4 className={styles.remediationTitle}>
                    Pièces déposées ({axeFiles?.length ?? 0})
                  </h4>
                  <FileInputButton
                    variant="text"
                    icon={<AddIcon />}
                    className={styles.download}
                    onChange={handleAddFile}
                  >
                    Ajouter une pièce
                  </FileInputButton>
                  <Button
                    variant="text"
                    icon={<Download />}
                    className={styles.download}
                    onClick={handleZip}
                  >
                    Télécharger toutes les pièces
                  </Button>
                </div>
              </FormField>
            </FormRow>
            <FormRow>
              <FormField>
                <div className={styles.depot}>
                  {sortedRemediationsDescending.length > 0 && (
                    <div className={styles.remediationStyleContainer}>
                      {sortedRemediationsDescending.map((remediation) => (
                        <div key={remediation.id}>
                          <div className={`${styles.header}`}>
                            <h4 className={styles.remediationTitle}>
                              Déposées suite à la remédiation du{" "}
                              {formatDate(remediation.creationDate)}
                            </h4>
                            <Button
                              variant="text"
                              icon={<Download />}
                              className={styles.download}
                              onClick={() =>
                                handleZipRemediation(
                                  groupedFiles[remediation.id],
                                  formatDate(remediation.creationDate)
                                )
                              }
                            >
                              Télécharger
                            </Button>
                            <div
                              className={styles.header}
                              onClick={() => toggleGroup(remediation.id)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => {
                                if (e.key === "Enter")
                                  toggleGroup(remediation.id);
                              }}
                            >
                              <Button
                                variant="text"
                                icon={
                                  groupStates[remediation.id] ? (
                                    <ChevronUp />
                                  ) : (
                                    <ChevronDown />
                                  )
                                }
                              >
                                {groupStates[remediation.id]
                                  ? "Voir moins"
                                  : "Voir plus"}
                              </Button>
                            </div>
                          </div>
                          {groupStates[remediation.id] && (
                            <div>
                              {groupedFiles[remediation.id] &&
                              groupedFiles[remediation.id].length > 0 ? (
                                groupedFiles[remediation.id].map((file) => (
                                  <div
                                    key={file.pathname}
                                    className={styles.file}
                                  >
                                    <p>{file.filename}</p>
                                    <p className={styles.fileDate}>
                                      Déposé le {formatDate(file.createdAt)}
                                    </p>
                                  </div>
                                ))
                              ) : (
                                <p className={styles.file}>
                                  Aucun fichier pour cette remédiation.
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className={`${styles.separation}`}>
                    <div
                      className={styles.header}
                      onClick={() => toggleGroup("initial")}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") toggleGroup("initial");
                      }}
                    >
                      <h4 className={styles.remediationTitle}>
                        Fichiers de dépôt initial
                      </h4>
                      <Button
                        variant="text"
                        icon={<Download />}
                        className={styles.download}
                        onClick={() =>
                          handleZipRemediation(
                            groupedFiles.initial,
                            "Fichiers de dépôt initial"
                          )
                        }
                      >
                        Télécharger
                      </Button>
                      <Button
                        variant="text"
                        iconVariant="left"
                        icon={
                          groupStates.initial ? <ChevronUp /> : <ChevronDown />
                        }
                      >
                        {groupStates.initial ? "Voir moins" : "Voir plus"}
                      </Button>
                    </div>
                    {groupStates.initial &&
                      groupedFiles.initial?.map((file) => (
                        <div key={file.pathname} className={styles.file}>
                          <p>{file.filename}</p>
                          <p className={styles.fileDate}>
                            Déposé le {formatDate(file.createdAt)}
                          </p>
                        </div>
                      ))}
                  </div>
                </div>
              </FormField>
            </FormRow>
          </div>
        </FormGrid>

        <Card className={styles.axeComponent}>
          <ComponentAxe
            controleQuinquennal={controleQuinquennal}
            axe={axe}
            entreprise={entreprise}
            setIsValid={setIsValid}
          />
        </Card>
      </div>
    </ControleQuinquennalTemplate>
  );
}
